import { useQuery } from "@apollo/client";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { AssociatedFacilityGrid } from "components/AssociateFacilities/AssociatedFacilityGrid";
import { AssociateFacilitiesDialog } from "components/AssociateFacilities/AssociateFacilitiesDialog";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { FACILITIES_BY_INTERNAL_TAG_NAME_QUERY } from "queries/facilitiesByInternalTagName";
import { useEffect, useState } from "react";
import { Controller, FormProvider, Resolver, useForm } from "react-hook-form";

export interface TagFormType {
  tagName: string;
  newTagName: string;
  facilityIds: string[];
}

interface TagDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (tagData: TagFormType) => void;
  tag?: {
    name: string;
    facilityCount: number;
  } | null;
  saving: boolean;
  validationResolver?: Resolver<TagFormType>;
}

export const TagDialog = ({
  open,
  onClose,
  onSave,
  tag,
  saving,
  validationResolver,
}: TagDialogProps) => {
  const isEdit = !!tag;
  const title = isEdit ? "Edit Tag" : "Add Tag";

  const {
    data: associatedFacilitiesData,
    loading: associatedFacilitiesLoading,
  } = useQuery(FACILITIES_BY_INTERNAL_TAG_NAME_QUERY, {
    variables: {
      tagName: tag?.name || "",
    },
    skip: !isEdit || !tag?.name,
    fetchPolicy: "network-only",
  });

  const defaultValues: TagFormType = {
    tagName: tag?.name ?? "",
    newTagName: tag?.name ?? "",
    facilityIds: [],
  };

  const form = useForm<TagFormType>({
    defaultValues,
    resolver: validationResolver,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = form;

  const [showAssociateFacilitiesModal, setShowAssociateFacilitiesModal] =
    useState(false);

  useEffect(() => {
    if (isEdit && tag && associatedFacilitiesData) {
      const facilities =
        associatedFacilitiesData.facilitiesByInternalTagName?.items || [];
      const facilityIds = facilities.map((facility: any) => facility.id);
      setValue("facilityIds", facilityIds);
    }
  }, [isEdit, tag, associatedFacilitiesData, setValue]);

  const openAssociateFacilitiesModal = () => {
    setShowAssociateFacilitiesModal(true);
  };

  const closeAssociateFacilitiesModal = () => {
    setShowAssociateFacilitiesModal(false);
  };

  const associatedFacilitiesUpdated = (facilityIds: string[]) => {
    form.setValue("facilityIds", facilityIds, { shouldDirty: true });
  };

  const onSubmit = async (data: TagFormType) => {
    onSave({
      ...data,
      facilityIds: data.facilityIds,
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        title={title}
        primaryActionButtonProps={{
          onClick: handleSubmit(onSubmit),
          disabled: !isDirty || saving || associatedFacilitiesLoading,
          loading: saving,
        }}
      >
        <FormProvider {...form}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? "Update the tag name and associated facilities below."
                : "Create a new internal tag and associate it with facilities."}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="newTagName"
                  label="Tag Name"
                  control={control}
                  disabled={associatedFacilitiesLoading}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2">
                    Associated Facilities
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={openAssociateFacilitiesModal}
                    startIcon={<AddCircleOutlineRounded />}
                    disabled={associatedFacilitiesLoading}
                  >
                    Associate Facilities
                  </Button>
                </Stack>

                <Controller
                  name="facilityIds"
                  control={control}
                  render={({
                    fieldState,
                    field: { value: facilityIds, onChange },
                  }) => {
                    return (
                      <Stack spacing={1}>
                        <AssociatedFacilityGrid
                          facilityIds={facilityIds ?? []}
                          onDelete={(id) => {
                            onChange(facilityIds?.filter((f) => f !== id));
                          }}
                          includeTenantFilter={false}
                          noRowsMessage="No facilities are currently associated with this tag."
                          facilityIdsLoading={associatedFacilitiesLoading}
                          showTenantColumn={true}
                        />
                        {fieldState.error && (
                          <Typography color="error" variant="caption">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </Stack>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </FormProvider>
        <AssociateFacilitiesDialog
          open={showAssociateFacilitiesModal}
          onClose={closeAssociateFacilitiesModal}
          onSave={associatedFacilitiesUpdated}
          existingFacilityIds={form.getValues("facilityIds")}
          allFacilities={true}
          showTenantColumn={true}
          subtitle="Select facilities to associate with this tag."
        />
      </Dialog>
    </>
  );
};
